import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { scrollToBooking } from "../utils/scrollToSection";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Breadcrumbs,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  LocationOn as LocationIcon,
  ArrowForward as ArrowIcon,
  Event as EventIcon,
  NavigateNext as NavNextIcon
} from "@mui/icons-material";
import { getCountry } from "../data/locationData";
import { getAllCities } from "../data/cityData";
import Navbar from "../components/navbar.jsx";
import Footer from "../components/footer";
import BookingForm from "../components/bookingForm";

const CountryPage = () => {
  const { continentSlug, countrySlug } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const result = getCountry(continentSlug, countrySlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [continentSlug, countrySlug]);

  if (!result) {
    return (
      <>
        <Navbar />
        <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h4">Location not found</Typography>
          <Button component={Link} to="/" sx={{ mt: 2 }}>Return Home</Button>
        </Container>
        <Footer />
      </>
    );
  }

  const { country, continent } = result;

  // For UK, use legacy city data
  const legacyCities = country.useLegacyCities ? getAllCities() : [];
  const cities = country.useLegacyCities ? legacyCities : country.cities;


  return (
    <>
      <Helmet>
        <title>{country.metaTitle}</title>
        <meta name="description" content={country.metaDescription} />
        <meta name="keywords" content={`dhol players ${country.name}, dhol drummers ${country.name}, Asian wedding entertainment ${country.name}, Indian wedding drummers ${country.name}, Punjabi dhol ${country.name}`} />
        <link rel="canonical" href={`https://imperialdrummers.com/dhol-players/${continent.slug}/${country.slug}`} />

        <meta property="og:title" content={country.metaTitle} />
        <meta property="og:description" content={country.metaDescription} />
        <meta property="og:url" content={`https://imperialdrummers.com/dhol-players/${continent.slug}/${country.slug}`} />
        <meta property="og:type" content="website" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": `Imperial Drummers - ${country.name}`,
            "description": country.metaDescription,
            "url": `https://imperialdrummers.com/dhol-players/${continent.slug}/${country.slug}`,
            "provider": {
              "@type": "Organization",
              "name": "Imperial Drummers",
              "url": "https://imperialdrummers.com"
            },
            "areaServed": {
              "@type": "Country",
              "name": country.name
            },
            "serviceType": "Dhol Entertainment"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://imperialdrummers.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": continent.name,
                "item": `https://imperialdrummers.com/dhol-players/${continent.slug}`
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": country.name,
                "item": `https://imperialdrummers.com/dhol-players/${continent.slug}/${country.slug}`
              }
            ]
          })}
        </script>
      </Helmet>

      <Navbar />

      <Box component="main">
        {/* Hero Section */}
        <Box sx={{
          background: 'linear-gradient(135deg, #5C005D 0%, #1a0a2e 100%)',
          color: 'white',
          pt: { xs: 14, md: 18 },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Container maxWidth="lg">
            {/* Breadcrumb */}
            <Breadcrumbs
              separator={<NavNextIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />}
              sx={{ mb: 3 }}
            >
              <Link to="/" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>
                Home
              </Link>
              <Link
                to={`/dhol-players/${continent.slug}`}
                style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}
              >
                {continent.name}
              </Link>
              <Typography sx={{ color: '#ffd700' }}>
                {country.name}
              </Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationIcon sx={{ mr: 1, color: '#ffd700' }} />
              <Typography variant="subtitle1" sx={{ color: '#ffd700' }}>
                {country.name}, {continent.name}
              </Typography>
            </Box>

            <Typography
              variant={isMobile ? "h3" : "h1"}
              component="h1"
              sx={{ fontWeight: 'bold', mb: 3 }}
            >
              Professional Dhol Players in {country.name}
            </Typography>

            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, lineHeight: 1.7, maxWidth: 800 }}>
              {country.description}
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={scrollToBooking}
              startIcon={<EventIcon />}
              color="gold"
              sx={{
                px: 4,
                py: 1.5,
                boxShadow: '0 8px 20px rgba(255, 215, 0, 0.3)',
              }}
            >
              Get Free Quote
            </Button>
          </Container>
        </Box>

        {/* Cities - UK uses card grid, international uses pills */}
        {country.useLegacyCities && cities.length > 0 && (
          <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: '#f8f5fb' }}>
            <Container maxWidth="lg">
              <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '1.75rem', md: '3rem' } }}>
                Dhol Players Across the {country.name}
              </Typography>
              <Typography variant="h6" align="center" sx={{ color: '#666', mb: 6, maxWidth: 700, mx: 'auto' }}>
                Select a city to find dhol players near you
              </Typography>

              <Grid container spacing={3}>
                {cities.map((city) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={city.slug}>
                    <Card
                      component={Link}
                      to={`/dhol-players/${city.slug}`}
                      sx={{
                        textDecoration: 'none',
                        display: 'block',
                        height: '100%',
                        '&:hover': { transform: 'translateY(-4px)', transition: '0.3s', boxShadow: 4 }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 2.5 }}>
                        <LocationIcon sx={{ color: '#5C005D', fontSize: 28, mb: 1 }} />
                        <Typography variant="h6" sx={{ color: '#5C005D', fontSize: '1rem' }}>
                          {city.name}
                        </Typography>
                        {city.region && (
                          <Typography variant="body2" color="text.secondary">
                            {city.region}
                          </Typography>
                        )}
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5C005D' }}>
                          <Typography variant="body2">View</Typography>
                          <ArrowIcon sx={{ fontSize: 14, ml: 0.5 }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        )}

        {/* International countries: pill/chip layout for all cities */}
        {!country.useLegacyCities && (
          <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: '#f8f5fb' }}>
            <Container maxWidth="lg">
              <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '1.75rem', md: '3rem' } }}>
                Cities We Serve in {country.name}
              </Typography>
              <Typography variant="h6" align="center" sx={{ color: '#666', mb: 6, maxWidth: 700, mx: 'auto' }}>
                Professional dhol players available throughout {country.name}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5 }}>
                {/* Linked cities (those with dedicated pages) */}
                {country.cities.map((city) => (
                  <Chip
                    key={city.slug}
                    label={city.name}
                    component={Link}
                    to={`/dhol-players/${continent.slug}/${country.slug}/${city.slug}`}
                    clickable
                    sx={{
                      backgroundColor: '#5C005D',
                      color: 'white',
                      fontSize: '1rem',
                      py: 2.5,
                      px: 1,
                      fontWeight: 500,
                      textDecoration: 'none',
                      '&:hover': { backgroundColor: '#7a007b' }
                    }}
                  />
                ))}
                {/* Additional major cities (non-linked pills) */}
                {(country.majorCities || []).map((cityName) => (
                  <Chip
                    key={cityName}
                    label={cityName}
                    sx={{
                      backgroundColor: '#5C005D',
                      color: 'white',
                      fontSize: '1rem',
                      py: 2.5,
                      px: 1,
                      '&:hover': { backgroundColor: '#7a007b' }
                    }}
                  />
                ))}
              </Box>
            </Container>
          </Box>
        )}

        {/* About section */}
        <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: 'white' }}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h2" sx={{ color: '#5C005D', mb: 3, fontSize: { xs: '1.75rem', md: '3rem' } }}>
                  Your {country.name} Dhol Experts
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: '1.1rem' }}>
                  Imperial Drummers has extensive experience performing at weddings and events
                  in {country.name}. Our professional dhol players are trained in the UK and bring
                  world-class entertainment to every celebration.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: '1.1rem' }}>
                  With over 2000 events performed globally, we understand the logistics of
                  international performances and work closely with your event planners to ensure
                  a seamless experience.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={scrollToBooking}
                  color="gold"
                  sx={{
                    px: 4,
                    py: 1.5,
                    boxShadow: '0 8px 20px rgba(255, 215, 0, 0.2)',
                  }}
                >
                  Book Dhol Players in {country.name}
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 4, backgroundColor: '#f8f5fb', borderRadius: 2 }}>
                  <Typography variant="h5" sx={{ color: '#5C005D', mb: 3 }}>
                    What's Included
                  </Typography>
                  {[
                    "Professional dhol players (solo to full ensemble)",
                    "Authentic traditional costumes",
                    "Premium dhol instruments",
                    "International travel coordination",
                    "Coordination with venue & planners",
                    "Flexible performance duration"
                  ].map((item, i) => (
                    <Typography key={i} variant="body1" sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
                      <Box component="span" sx={{ color: '#ffd700', mr: 1, fontWeight: 'bold' }}>&#10003;</Box>
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Booking Form */}
        <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: '#f8f5fb' }}>
          <BookingForm />
        </Box>

        <Footer />
      </Box>
    </>
  );
};

export default CountryPage;
